/**
* จุดนัดพบในศูนย์การค้าชั้นนำ — กรุงเทพฯ, ปริมณฑล และต่างจังหวัด
*
* COMPLIANCE: ทุกจุดเป็น service area / จุดนัดหมายเท่านั้น
* (`serviceAreaOnly: true`) ไม่มี street / postalCode — เราไม่ได้เช่าพื้นที่
* ถาวรในศูนย์การค้าเหล่านี้ จึงห้ามประกาศเป็นที่อยู่สำนักงานโดยเด็ดขาด
*/
import { branch, type Branch, type ServiceFocus } from "./types";

const FOCUS_MALL: ServiceFocus[] = [
  "notary",
  "translation",
  "sworn",
  "lgz",
  "moj",
  "pcc",
  "visa",
];
const FOCUS_TOURIST: ServiceFocus[] = [
  "notary",
  "translation",
  "sworn",
  "naati",
  "lgz",
  "visa",
  "longstay",
  "marriage",
];

/** [id, ชื่อศูนย์การค้า, provinceSlug, จังหวัด/อำเภอที่ตั้ง, districtSlug?, transit?, tourist?] */
type M = [string, string, string, string, string?, string?, boolean?];

const MALLS: M[] = [
  // ── กรุงเทพมหานคร ────────────────────────────────────────────
  ["iconsiam", "ไอคอนสยาม", "bangkok", "เขตคลองสาน", "khlong-san", "BTS สายสีทอง เจริญนคร", true],
  ["emquartier", "ดิ เอ็มควอเทียร์", "bangkok", "เขตวัฒนา", "watthana", "BTS พร้อมพงษ์", true],
  ["emporium", "ดิ เอ็มโพเรียม", "bangkok", "เขตคลองเตย", "khlong-toei", "BTS พร้อมพงษ์", true],
  ["terminal21-asok", "เทอร์มินอล 21 อโศก", "bangkok", "เขตวัฒนา", "watthana", "BTS อโศก / MRT สุขุมวิท", true],
  ["central-embassy", "เซ็นทรัล เอ็มบาสซี", "bangkok", "เขตปทุมวัน", "pathum-wan", "BTS เพลินจิต", true],
  ["central-chidlom", "เซ็นทรัล ชิดลม", "bangkok", "เขตปทุมวัน", "pathum-wan", "BTS ชิดลม", true],
  ["mbk", "เอ็มบีเค เซ็นเตอร์", "bangkok", "เขตปทุมวัน", "pathum-wan", "BTS สนามกีฬาแห่งชาติ", true],
  ["siam-discovery", "สยามดิสคัฟเวอรี่", "bangkok", "เขตปทุมวัน", "pathum-wan", "BTS สยาม"],
  ["siam-center", "สยามเซ็นเตอร์", "bangkok", "เขตปทุมวัน", "pathum-wan", "BTS สยาม"],
  ["central-rama9", "เซ็นทรัล พระราม 9", "bangkok", "เขตห้วยขวาง", "huai-khwang", "MRT พระราม 9"],
  ["central-ladprao", "เซ็นทรัล ลาดพร้าว", "bangkok", "เขตจตุจักร", "chatuchak", "BTS ห้าแยกลาดพร้าว / MRT พหลโยธิน"],
  ["union-mall", "ยูเนี่ยน มอลล์", "bangkok", "เขตจตุจักร", "chatuchak", "MRT พหลโยธิน"],
  ["central-westgate-bkk", "เซ็นทรัล เวสต์เกต (ฝั่งกรุงเทพฯ)", "nonthaburi", "อำเภอบางใหญ่", undefined, "MRT คลองบางไผ่"],
  ["central-pinklao", "เซ็นทรัล ปิ่นเกล้า", "bangkok", "เขตบางกอกน้อย", "bangkok-noi", "MRT บางยี่ขัน"],
  ["central-bangna", "เซ็นทรัล บางนา", "bangkok", "เขตบางนา", "bang-na", "BTS อุดมสุข"],
  ["mega-bangna", "เมกาบางนา", "samut-prakan", "อำเภอบางพลี", undefined, "BTS แบริ่ง (ต่อรถ)"],
  ["seacon-square", "ซีคอนสแควร์ ศรีนครินทร์", "bangkok", "เขตประเวศ", "prawet", "MRT สายสีเหลือง สวนหลวง ร.9"],
  ["paradise-park", "พาราไดซ์ พาร์ค", "bangkok", "เขตประเวศ", "prawet", "MRT สายสีเหลือง ศรีอุดม"],
  ["the-mall-bangkapi", "เดอะมอลล์ บางกะปิ", "bangkok", "เขตบางกะปิ", "bang-kapi", "MRT สายสีเหลือง แยกลำสาลี"],
  ["fashion-island", "แฟชั่นไอส์แลนด์", "bangkok", "เขตคันนายาว", "khan-na-yao", "MRT สายสีชมพู วงแหวนรามอินทรา"],
  ["central-rama2", "เซ็นทรัล พระราม 2", "bangkok", "เขตบางขุนเทียน", "bang-khun-thian"],
  ["central-rama3", "เซ็นทรัล พระราม 3", "bangkok", "เขตยานนาวา", "yan-nawa"],
  ["central-salaya", "เซ็นทรัล ศาลายา", "nakhon-pathom", "อำเภอพุทธมณฑล"],
  ["future-park", "ฟิวเจอร์พาร์ค รังสิต", "pathum-thani", "อำเภอธัญบุรี", undefined, "BTS คูคต (ต่อรถ)"],
  ["central-chaengwattana", "เซ็นทรัล แจ้งวัฒนะ", "nonthaburi", "อำเภอปากเกร็ด", undefined, "MRT สายสีชมพู ศูนย์ราชการ"],
  ["the-mall-ngamwongwan", "เดอะมอลล์ งามวงศ์วาน", "nonthaburi", "อำเภอเมืองนนทบุรี"],
  ["central-westgate", "เซ็นทรัล เวสต์เกต", "nonthaburi", "อำเภอบางใหญ่", undefined, "MRT คลองบางไผ่"],
  ["imperial-samrong", "อิมพีเรียล เวิลด์ สำโรง", "samut-prakan", "อำเภอเมืองสมุทรปราการ", undefined, "BTS สำโรง"],
  ["central-mahachai", "เซ็นทรัล มหาชัย", "samut-sakhon", "อำเภอเมืองสมุทรสาคร"],
  ["central-ayutthaya", "เซ็นทรัล อยุธยา", "ayutthaya", "อำเภอพระนครศรีอยุธยา"],

  // ── ภาคเหนือ ────────────────────────────────────────────────
  ["central-chiangmai-festival", "เซ็นทรัล เชียงใหม่ (เฟสติวัล)", "chiang-mai", "อำเภอเมืองเชียงใหม่", undefined, undefined, true],
  ["central-chiangmai-airport", "เซ็นทรัล เชียงใหม่ แอร์พอร์ต", "chiang-mai", "อำเภอเมืองเชียงใหม่", undefined, undefined, true],
  ["maya-chiangmai", "เมญ่า ไลฟ์สไตล์ ช้อปปิ้งเซ็นเตอร์", "chiang-mai", "อำเภอเมืองเชียงใหม่", undefined, undefined, true],
  ["central-chiangrai", "เซ็นทรัล เชียงราย", "chiang-rai", "อำเภอเมืองเชียงราย", undefined, undefined, true],
  ["central-lampang", "เซ็นทรัล ลำปาง", "lampang", "อำเภอเมืองลำปาง"],
  ["central-phitsanulok", "เซ็นทรัล พิษณุโลก", "phitsanulok", "อำเภอเมืองพิษณุโลก"],
  ["robinson-mae-sot", "โรบินสัน แม่สอด", "tak", "อำเภอแม่สอด"],
  ["v-square-nan", "ศูนย์การค้าใจกลางเมืองน่าน", "nan", "อำเภอเมืองน่าน"],
  ["central-nakhonsawan", "เซ็นทรัล นครสวรรค์", "nakhon-sawan", "อำเภอเมืองนครสวรรค์"],

  // ── ภาคอีสาน ────────────────────────────────────────────────
  ["central-khonkaen", "เซ็นทรัล ขอนแก่น", "khon-kaen", "อำเภอเมืองขอนแก่น"],
  ["central-udon", "เซ็นทรัล อุดรธานี", "udon-thani", "อำเภอเมืองอุดรธานี", undefined, undefined, true],
  ["central-korat", "เซ็นทรัล นครราชสีมา", "nakhon-ratchasima", "อำเภอเมืองนครราชสีมา"],
  ["terminal21-korat", "เทอร์มินอล 21 โคราช", "nakhon-ratchasima", "อำเภอเมืองนครราชสีมา"],
  ["central-ubon", "เซ็นทรัล อุบลราชธานี", "ubon-ratchathani", "อำเภอเมืองอุบลราชธานี"],
  ["robinson-buriram", "โรบินสัน บุรีรัมย์", "buri-ram", "อำเภอเมืองบุรีรัมย์"],
  ["robinson-surin", "โรบินสัน สุรินทร์", "surin", "อำเภอเมืองสุรินทร์"],
  ["robinson-roi-et", "โรบินสัน ร้อยเอ็ด", "roi-et", "อำเภอเมืองร้อยเอ็ด"],
  ["robinson-sakon", "โรบินสัน สกลนคร", "sakon-nakhon", "อำเภอเมืองสกลนคร"],
  ["robinson-mukdahan", "โรบินสัน มุกดาหาร", "mukdahan", "อำเภอเมืองมุกดาหาร"],
  ["assawann-nongkhai", "อัศวรรณ ช้อปปิ้ง คอมเพล็กซ์ หนองคาย", "nong-khai", "อำเภอเมืองหนองคาย"],
  ["esplanade-mahasarakham", "ศูนย์การค้าใจกลางเมืองมหาสารคาม", "maha-sarakham", "อำเภอเมืองมหาสารคาม"],

  // ── ภาคตะวันออก ─────────────────────────────────────────────
  ["central-pattaya-beach", "เซ็นทรัล พัทยา บีช", "chon-buri", "อำเภอบางละมุง", undefined, undefined, true],
  ["terminal21-pattaya", "เทอร์มินอล 21 พัทยา", "chon-buri", "อำเภอบางละมุง", undefined, undefined, true],
  ["central-chonburi", "เซ็นทรัล ชลบุรี", "chon-buri", "อำเภอเมืองชลบุรี"],
  ["central-sriracha", "เซ็นทรัล ศรีราชา", "chon-buri", "อำเภอศรีราชา", undefined, undefined, true],
  ["central-rayong", "เซ็นทรัล ระยอง", "rayong", "อำเภอเมืองระยอง", undefined, undefined, true],
  ["central-chanthaburi", "เซ็นทรัล จันทบุรี", "chanthaburi", "อำเภอเมืองจันทบุรี"],
  ["robinson-chachoengsao", "โรบินสัน ฉะเชิงเทรา", "chachoengsao", "อำเภอเมืองฉะเชิงเทรา"],
  ["robinson-prachinburi", "โรบินสัน ปราจีนบุรี", "prachin-buri", "อำเภอเมืองปราจีนบุรี"],
  ["robinson-trat", "โรบินสัน ตราด", "trat", "อำเภอเมืองตราด"],

  // ── ภาคตะวันตก / ใต้ ────────────────────────────────────────
  ["central-phuket-floresta", "เซ็นทรัล ภูเก็ต ฟลอเรสต้า", "phuket", "อำเภอเมืองภูเก็ต", undefined, undefined, true],
  ["jungceylon-patong", "จังซีลอน ป่าตอง", "phuket", "อำเภอกะทู้", undefined, undefined, true],
  ["central-samui", "เซ็นทรัล สมุย", "surat-thani", "อำเภอเกาะสมุย", undefined, undefined, true],
  ["central-surat", "เซ็นทรัล สุราษฎร์ธานี", "surat-thani", "อำเภอเมืองสุราษฎร์ธานี"],
  ["central-hatyai", "เซ็นทรัล หาดใหญ่", "songkhla", "อำเภอหาดใหญ่", undefined, undefined, true],
  ["central-nakhonsithammarat", "เซ็นทรัล นครศรีธรรมราช", "nakhon-si-thammarat", "อำเภอเมืองนครศรีธรรมราช"],
  ["robinson-trang", "โรบินสัน ตรัง", "trang", "อำเภอเมืองตรัง"],
  ["vogue-krabi", "โว้ค ช้อปปิ้ง เซ็นเตอร์ กระบี่", "krabi", "อำเภอเมืองกระบี่", undefined, undefined, true],
  ["central-hua-hin", "เซ็นทรัล หัวหิน", "prachuap-khiri-khan", "อำเภอหัวหิน", undefined, undefined, true],
  ["robinson-phetchaburi", "โรบินสัน เพชรบุรี", "phetchaburi", "อำเภอเมืองเพชรบุรี"],
  ["robinson-kanchanaburi", "โรบินสัน กาญจนบุรี", "kanchanaburi", "อำเภอเมืองกาญจนบุรี"],
  ["central-ratchaburi", "เซ็นทรัล ราชบุรี", "ratchaburi", "อำเภอเมืองราชบุรี"],
  ["robinson-chumphon", "โรบินสัน ชุมพร", "chumphon", "อำเภอเมืองชุมพร"],
  ["central-phangnga", "ศูนย์การค้าใจกลางเมืองพังงา", "phang-nga", "อำเภอเมืองพังงา"],
];

export const MALL_BRANCHES: Branch[] = MALLS.map(
  ([id, mall, provinceSlug, locality, districtSlug, transit, tourist]) =>
    branch({
      id: `mall-${id}`,
      name: `จุดนัดพบ ${mall}`,
      shortName: mall,
      street: "",
      locality,
      region: provinceSlug === "bangkok" ? "กรุงเทพมหานคร" : locality,
      postalCode: "",
      serviceAreaOnly: true,
      meetingPoint: `บริเวณ${mall} (นัดหมายล่วงหน้า ระบุชั้น/จุดนัดพบก่อนเข้าพบ)`,
      branchType: "mall",
      provinceSlug,
      districtSlug,
      landmarks: [mall],
      transit: transit ? [transit] : [],
      serviceFocus: tourist ? FOCUS_TOURIST : FOCUS_MALL,
      note: `นัดเซ็นเอกสารต่อหน้า Notarial Services Attorney และรับ-ส่งเอกสารบริเวณ${mall}`,
    }),
);
